import classNames from "classnames";
import styles from "./Link.module.scss";

import { LinkProps, Link as RRDLink, useNavigate } from "react-router-dom";
import { palette } from "../../../styles/theme";
import { Box } from "../Box";
import { Icon } from "../Icon";
import { MaterialSymbolType } from "../Icon/IconConstant";
import { PropsWithDataTest } from "../../../types";
import { CSSProperties } from "react";

type PropsWithTo = Omit<LinkProps, "to" | "onClick"> & {
  to: string;
};
type PropsWithOnClick = Omit<LinkProps, "to" | "onClick"> & {
  onClick: LinkProps["onClick"];
  to?: never;
};

type Props = Omit<LinkProps, "to" | "onClick"> & {
  smallFont?: boolean;
  startIcon?: MaterialSymbolType;
  endIcon?: MaterialSymbolType;
  iconStyle?: CSSProperties;
  alwaysUnderline?: boolean;
} & (PropsWithTo | PropsWithOnClick) &
  PropsWithDataTest;

export const Link = ({
  smallFont,
  startIcon,
  endIcon,
  alwaysUnderline,
  iconStyle,
  "data-test": dataTest,
  ...props
}: Props) => {
  const navigate = useNavigate();
  return startIcon || endIcon ? (
    <Box
      data-test={dataTest}
      display={"flex"}
      justifyContent={"flex-start"}
      width={"fit-content"}
      sx={{
        display: "flex",
        gap: "4px",
        alignItems: "center",
        cursor: "pointer",
        textDecoration: alwaysUnderline ? "underline" : "none",
        "&:hover": {
          a: {
            textDecoration: "underline",
          },
        },
      }}
      onClick={(e) => {
        if (props.to) {
          e.preventDefault();
          if (props.target === "_blank") {
            window.open(props.to ?? "", "_blank");
          } else {
            navigate(props.to ?? "");
          }
          return;
        }
        if ("onClick" in props) {
          props.onClick?.(e as any);
        }
      }}
    >
      {startIcon && (
        <Icon
          icon={startIcon}
          color={palette.blue.secondary}
          style={{ textDecoration: "none !important", ...iconStyle }}
        />
      )}
      <RRDLink
        {...props}
        to={""}
        onClick={(e) => e.preventDefault()}
        className={classNames(props.className, styles.link, {
          [styles.smallFont]: smallFont,
        })}
      />
      {endIcon && (
        <Icon
          icon={endIcon}
          color={palette.blue.secondary}
          style={{ textDecoration: "none !important", ...iconStyle }}
        />
      )}
    </Box>
  ) : (
    <RRDLink
      data-test={dataTest}
      {...props}
      to={props.to ?? ""}
      className={classNames(props.className, styles.link, {
        [styles.smallFont]: smallFont,
        [styles.alwaysUnderline]: alwaysUnderline,
      })}
    />
  );
};
